import dayjs from 'dayjs';
import { union, difference, random } from 'lodash';
import { UNLIMITED_LOCAL_STORAGE_KEY } from 'common/constants';

const PT_START_DATE = '2024-09-01';
const EN_START_DATE = '2024-09-01';
const ES_START_DATE = '2024-09-01';
const NO_START_DATE = '2025-09-01';

const SIMULATED_DATE = '';

export const getToday = () => {
  if (process.env.NODE_ENV === 'development' && SIMULATED_DATE) {
    return dayjs(SIMULATED_DATE, 'YYYY-MM-DD')
      .hour(dayjs().hour())
      .minute(dayjs().minute());
  }
  return dayjs();
};

export const getStartDate = (language) => {
  let startDate = dayjs(PT_START_DATE, 'YYYY-MM-DD');
  if (language === 'en') {
    startDate = dayjs(EN_START_DATE, 'YYYY-MM-DD');
  } else if (language === 'es') {
    startDate = dayjs(ES_START_DATE, 'YYYY-MM-DD');
  } else if (language === 'no') {
    startDate = dayjs(NO_START_DATE, 'YYYY-MM-DD');
  }
  // For testing before the release date
  if (getToday().isBefore(startDate)) {
    startDate = getToday();
  }
  return startDate;
};

export const getGameId = (date, language) => {
  const initialTime = getStartDate(language).startOf('day');
  const currentTime = dayjs(date, 'YYYY-MM-DD').startOf('day');
  const gameId = currentTime.diff(initialTime, 'day');
  return gameId;
};

export const getTodaysGameId = (language) => {
  const initialTime = getStartDate(language).startOf('day');
  const currentTime = getToday().startOf('day');
  const gameId = currentTime.diff(initialTime, 'day');
  return `${gameId}`;
};

export const getChart = (gameState) => {
  const { answer } = gameState.spec;
  const { attempts } = gameState.data;
  const attemptsData = getAttemptsData(answer, attempts);
  let chart = '';
  for (let i = 0; i < attemptsData.length - 1; i++) {
    const attempt = attemptsData[i];
    for (let j = 0; j < attempt.length; j++) {
      const { classes } = attempt[j];
      if (classes.includes('absent')) {
        chart += '⬛';
      } else if (classes.includes('start') || classes.includes('end')) {
        chart += '🟢';
      } else if (classes.includes('head') || classes.includes('tail')) {
        chart += '🟩';
      } else if (classes.includes('present')) {
        chart += '🟨';
      }
    }
    chart += '\n';
  }
  chart += '✅';
  return chart;
};

export const removeAccents = (word) => {
  // Norwegian letters to preserve
  const norwegian = /[æøåÆØÅ]/;
  // Process each character: if Norwegian, keep as is; else, remove diacritics
  return Array.from(word)
    .map((char) =>
      norwegian.test(char)
        ? char
        : char.normalize('NFD').replace(/\p{Diacritic}/gu, ''),
    )
    .join('');
};

export const getDiff = (answerParam, guessParam) => {
  const answer = removeAccents(answerParam);
  const guess = removeAccents(guessParam);
  const table = Array.from({ length: answer.length + 1 }, () =>
    Array.from({ length: guess.length + 1 }, () => ({ cost: 0, paths: [] })),
  );

  table[0][0] = { cost: 0, paths: [[]] };
  for (let a = 1; a < answer.length + 1; a++)
    table[a][0] = {
      cost: a,
      paths: table[a - 1][0].paths.map((x) => [...x, '+']),
    };
  for (let b = 1; b < guess.length + 1; b++)
    table[0][b] = {
      cost: b,
      paths: table[0][b - 1].paths.map((x) => [...x, '-']),
    };

  for (let a = 1; a < answer.length + 1; a++) {
    for (let b = 1; b < guess.length + 1; b++) {
      const accept =
        table[a - 1][b - 1].cost +
        (answer[a - 1] === guess[b - 1] ? 0 : Infinity);
      const insert = table[a - 1][b].cost + 1;
      const remove = table[a][b - 1].cost + 1;

      const cost = Math.min(insert, remove, accept);
      const paths = [];

      if (cost === accept)
        paths.push(...table[a - 1][b - 1].paths.map((x) => [...x, '>']));
      if (cost === insert)
        paths.push(...table[a - 1][b].paths.map((x) => [...x, '+']));
      if (cost === remove)
        paths.push(...table[a][b - 1].paths.map((x) => [...x, '-']));

      table[a][b] = { cost, paths };
    }
  }

  let best_score = -Infinity;
  let best_results = [];

  table[answer.length][guess.length].paths.forEach((path) => {
    const start = path[0] === '>';
    const end = path[path.length - 1] === '>';
    const pattern = Array.from({ length: guess.length }, (x) => 0);
    const unused_letter = Array.from(answer);

    let accept_count = 0;
    let streak_length = 0;
    let score = 0;
    if (start) score += 1;
    if (end) score += 1;

    let b = 0;
    for (let i = 0; i < path.length; i++) {
      switch (path[i]) {
        case '>':
          accept_count++;
          streak_length++;
          pattern[b] = streak_length === 1 ? 2 : 3;
          unused_letter.splice(unused_letter.indexOf(guess[b]), 1);
          score += 3 * streak_length;
          b++;
          break;
        case '+':
          streak_length = 0;
          break;
        case '-':
          streak_length = 0;
          b++;
          break;
        default:
          break;
      }
    }

    for (let i = 0; i < guess.length; i++) {
      if (pattern[i] === 0 && unused_letter.includes(guess[i])) {
        pattern[i] = 1;
        unused_letter.splice(unused_letter.indexOf(guess[i]), 1);
      }
    }

    if (accept_count === 1 && !start && !end) {
      pattern[pattern.indexOf(2)] = 1;
    }

    if (best_score === score) {
      best_results.push({ pattern, start, end });
    } else if (best_score < score) {
      best_score = score;
      best_results = [{ pattern, start, end }];
    }
  });

  best_results.sort((a, b) => (a.pattern.join() < b.pattern.join() ? 1 : -1));
  return best_results[0];
};

export const getAttemptsData = (answer, attempts) => {
  return attempts.map((attempt) => {
    const diff = getDiff(answer, attempt);
    const letters = attempt.split('');
    const lettersData = letters.map((letter, i) => {
      const classes = ['letter'];
      classes.push(['absent', 'present', 'head', 'tail'][diff.pattern[i]]);
      if (i === 0 && diff.start) classes.push('start');
      if (i === attempt.length - 1 && diff.end) classes.push('end');
      return { letter, classes };
    });

    const presentIndices = [];
    let absentCount = 0;
    lettersData.forEach((item, i) => {
      if (item.classes.includes('present')) presentIndices.push(i);
      if (item.classes.includes('absent')) absentCount++;
    });
    if (presentIndices.length === 1 && absentCount === lettersData.length - 1) {
      const index = presentIndices[0];
      lettersData[index] = {
        ...lettersData[index],
        classes: lettersData[index].classes.map((c) =>
          c === 'present' ? 'one-present' : c,
        ),
      };
    }

    return lettersData;
  });
};

export const getAttemptCount = (gameState) => gameState.data.attempts.length;

export const getHintCount = (_) => 0;

export const verifyAndFixGameData = (gameSpec, gameData) => {
  if (!gameSpec || !gameData) {
    return gameData;
  }

  if (gameSpec.version !== gameData.version) return null;

  return gameData;
};

const excludedGameIds = {
  en: [],
  pt: [133], // Excluding ziguezague, which is misspelled
  es: [],
};

export const getUnlimitedGameId = (language) => {
  const todaysGameId = getTodaysGameId(language);
  const allGameIds = Array.from(
    { length: parseInt(todaysGameId) + 1 },
    (_, i) => i,
  );
  const recentGameIds = JSON.parse(
    localStorage.getItem(UNLIMITED_LOCAL_STORAGE_KEY) || '{}',
  );
  const unavailableGameIds = union(
    recentGameIds[language],
    excludedGameIds[language],
  );
  const availableGameIds = difference(allGameIds, unavailableGameIds);
  const randomIndex = random(availableGameIds.length - 1);
  const gameId = availableGameIds[randomIndex];
  if (!recentGameIds[language]) {
    recentGameIds[language] = [];
  }
  recentGameIds[language].push(gameId);
  while (recentGameIds[language].length > 50) {
    recentGameIds[language].shift();
  }
  localStorage.setItem(
    UNLIMITED_LOCAL_STORAGE_KEY,
    JSON.stringify(recentGameIds),
  );
  return gameId;
};
